import { type CSSProperties, type KeyboardEvent, type PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../../components/ui/Icon";

const WIDEST = 16 / 9;
// Why: 4:5 is the tallest frame Meta recommends for the feed; a taller video is shown cut to it here.
const TALLEST = 4 / 5;
const fmt = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

/**
 * An ad's video as Flashadz plays it: a round play button, then one glass bar (play, time, the line to move
 * along, sound, full screen) that fades while it plays. Sound starts off, as in the feed. In the preview it
 * takes the video's own shape, from wide (16:9) to 4:5; `fill` fits it whole in its box instead.
 */
export function AdPlayer({ src, label, fill, onShape }: { src: string; label: string; fill?: boolean; onShape?: (wide: boolean) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const time = useRef<HTMLSpanElement>(null);
  const idleTimer = useRef(0);
  const scrubbing = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(true);
  const [idle, setIdle] = useState(false);
  const [ratio, setRatio] = useState<number | null>(null);

  const paint = useCallback(() => {
    const v = video.current;
    if (!v || !box.current) return;
    const d = Number.isFinite(v.duration) ? v.duration : 0;
    box.current.style.setProperty("--p", String(d ? Math.min(1, v.currentTime / d) : 0));
    if (time.current) time.current.textContent = `${fmt(v.currentTime)} / ${fmt(d)}`;
  }, []);

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const loop = () => {
      paint();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, paint]);
  useEffect(() => () => clearTimeout(idleTimer.current), []);

  const wake = () => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      if (!scrubbing.current) setIdle(true);
    }, 1800);
  };
  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused || v.ended) void v.play().catch(() => undefined);
    else v.pause();
  };
  const sound = () => {
    if (video.current) video.current.muted = !video.current.muted;
  };
  const seek = (e: PointerEvent<HTMLDivElement>) => {
    const v = video.current;
    if (!v || !Number.isFinite(v.duration)) return;
    const r = e.currentTarget.getBoundingClientRect();
    v.currentTime = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * v.duration;
    paint();
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const v = video.current;
    if (!v) return;
    if (e.key === "m") return sound();
    if (e.target instanceof HTMLButtonElement) return;
    if (e.key === " " || e.key === "k") {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    }
    if (e.key === "ArrowRight") v.currentTime = Math.min(v.duration || 0, v.currentTime + 1);
    if (e.key === "ArrowLeft") v.currentTime = Math.max(0, v.currentTime - 1);
    paint();
  };

  const icon = ended ? "again" : playing ? "pause" : "play";
  const what = ended ? "Play again" : playing ? "Pause" : "Play";
  const shape: CSSProperties | undefined = fill || !ratio ? undefined : { aspectRatio: String(Math.min(WIDEST, Math.max(TALLEST, ratio))) };
  return (
    <div
      ref={box}
      // Why fs-norecord: the playhead moves every frame; Flashsuite's Show & tell leaves it out of the page's minutes.
      className={`apl fs-norecord${fill ? " fill" : ""}${playing ? " playing" : ""}${playing && idle ? " idle" : ""}`}
      style={shape}
      role="region"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={key}
      onPointerMove={wake}
      onPointerLeave={() => playing && setIdle(true)}
    >
      <video
        ref={video}
        // Why: the fragment makes Safari draw the first frame instead of black before play.
        src={`${src}#t=0.001`}
        playsInline
        muted
        preload="metadata"
        onClick={toggle}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (v.videoWidth && v.videoHeight) {
            setRatio(v.videoWidth / v.videoHeight);
            onShape?.(v.videoWidth > v.videoHeight);
          }
          paint();
        }}
        onPlay={() => {
          setPlaying(true);
          setEnded(false);
          wake();
        }}
        onPause={() => {
          setPlaying(false);
          paint();
        }}
        onEnded={() => {
          setPlaying(false);
          setEnded(true);
        }}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        onSeeked={paint}
      />
      <button className={`apl-big${ended ? "" : " solid"}`} type="button" aria-label={what} onClick={toggle}>
        <Icon name={icon} />
      </button>
      <div className="apl-bar">
        <button className={`apl-b${ended ? "" : " solid"}`} type="button" aria-label={what} onClick={toggle}>
          <Icon name={icon} />
        </button>
        <span className="apl-t" ref={time}>
          0:00 / 0:00
        </span>
        <div
          className="apl-track"
          aria-hidden="true"
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            scrubbing.current = true;
            box.current?.classList.add("scrub");
            seek(e);
          }}
          onPointerMove={(e) => {
            if (scrubbing.current) seek(e);
          }}
          onPointerUp={() => {
            scrubbing.current = false;
            box.current?.classList.remove("scrub");
            wake();
          }}
        >
          <span className="rail">
            <span className="fill" />
          </span>
          <span className="knob" />
        </div>
        <button className="apl-b" type="button" aria-label={muted ? "Turn sound on" : "Turn sound off"} onClick={sound}>
          <Icon name={muted ? "muted" : "sound"} />
        </button>
        <button className="apl-b" type="button" aria-label="Full screen" onClick={() => void box.current?.requestFullscreen?.().catch(() => undefined)}>
          <Icon name="full" />
        </button>
      </div>
    </div>
  );
}
